/**
 * SYM-6198. The arithmetic behind the crop rectangle, lifted out of the
 * component so it can be tested on its own.
 *
 * The reported fault is that reopening the cropper on a picture that has
 * already been cropped shows the rectangle a little lower than it was left,
 * and lower again on the next visit. The rectangle makes two trips on the way:
 * it is stored as percentages of the displayed picture, and read back as
 * pixels of the displayed picture, with a fit-to-shape step in between.
 *
 * These are the three pieces of that journey. Nothing here changed in
 * behaviour; it moved.
 */

export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PercentRect {
  unit: '%';
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The largest rectangle of the wanted shape, in the middle of the picture. */
export function centeredCropForAspect(
  w: number,
  h: number,
  a: number
): PixelRect & { unit: 'px' } {
  const imgAspect = w / h;
  const cw = imgAspect > a ? h * a : w;
  const ch = imgAspect > a ? h : w / a;

  return {
    unit: 'px' as const,
    x: (w - cw) / 2,
    y: (h - ch) / 2,
    width: cw,
    height: ch,
  };
}

/**
 * Bring a rectangle to the wanted shape without moving its middle, then push
 * it back inside the picture if that took it over an edge.
 *
 * A rectangle already of that shape is returned untouched, which is what keeps
 * a second visit from moving anything.
 */
export function fitPxCropToAspect(
  px: PixelRect,
  a: number,
  maxW: number,
  maxH: number
): PixelRect & { unit: 'px' } {
  const current = px.width / px.height;
  if (Math.abs(current - a) < 0.01) return { unit: 'px' as const, ...px };

  const cx = px.x + px.width / 2;
  const cy = px.y + px.height / 2;
  let nw: number;
  let nh: number;
  if (current > a) {
    nh = px.height;
    nw = nh * a;
  } else {
    nw = px.width;
    nh = nw / a;
  }
  // clamp so the rect stays inside the image
  nw = Math.min(nw, maxW);
  nh = Math.min(nh, maxH);
  let nx = cx - nw / 2;
  let ny = cy - nh / 2;
  if (nx < 0) nx = 0;
  if (ny < 0) ny = 0;
  if (nx + nw > maxW) nx = maxW - nw;
  if (ny + nh > maxH) ny = maxH - nh;

  return { unit: 'px' as const, x: nx, y: ny, width: nw, height: nh };
}

/** A rectangle in pixels of a displayed picture, as percentages of it. */
export function pxToPercent(px: PixelRect, w: number, h: number): PercentRect {
  return {
    unit: '%',
    x: (px.x / w) * 100,
    y: (px.y / h) * 100,
    width: (px.width / w) * 100,
    height: (px.height / h) * 100,
  };
}

/** The same rectangle back in pixels of a displayed picture of this size. */
export function percentToPx(
  crop: { x?: number; y?: number; width: number; height: number },
  w: number,
  h: number
): PixelRect {
  return {
    x: ((crop.x ?? 0) / 100) * w,
    y: ((crop.y ?? 0) / 100) * h,
    width: (crop.width / 100) * w,
    height: (crop.height / 100) * h,
  };
}
